import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { AccountSelector } from '@/experiences/sales-portal/components/account-selector';
import { AddCompetitor } from '@/experiences/sales-portal/domain/pricing/screens/quotes/components/add-competitor';
import {
  AnimatePresence,
  Button,
  Icons,
  useAlertContext,
} from '@usepepper/sprout-kit-ui';
import { AppPageLayout } from '@experiences/templates/app-page-layout';
import { BottomTabScreenProps } from '@react-navigation/bottom-tabs';
import {
  CompetitorData,
  QuoteLineItem,
} from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/hooks/use-quote-state-types';
import { createStyles } from '@/contexts/theme-context';
import { CustomItemPanel } from '@/experiences/sales-portal/domain/pricing/screens/quotes/components/custom-item-panel';
import { Divider } from '@usepepper/ui/atoms/divider';
import { LayoutChangeEvent, Platform, View } from 'react-native';
import { LayoutSize, useLayoutContext } from '@/contexts/layout-context';
import { NameQuoteModalContent } from '@/experiences/sales-portal/domain/pricing/screens/quotes/components/name-quote-modal-content';
import { partnerSupplier } from '@/utils/environment';
import { PriceQuotesEmptyState } from '@/experiences/sales-portal/domain/pricing/screens/quotes/components/price-quotes-empty-state';
import {
  PriceQuoteSummary,
  useSavedQuotesLoader,
} from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/loaders/use-saved-quotes-loader';
import { QuoteActionButtons } from '@/experiences/sales-portal/domain/pricing/screens/quotes/components/quote-action-buttons';
import { QuoteItemSearch } from '@/experiences/sales-portal/domain/pricing/screens/quotes/containers/quote-item-search';
import { QuoteLineItemsTable } from '@/experiences/sales-portal/domain/pricing/screens/quotes/components/quote-line-items-table';
import { QuoteLineItemsTableSkeleton } from '@/experiences/sales-portal/domain/pricing/screens/quotes/components/quote-line-items-table-skeleton';
import { QuoteNameEditor } from '@/experiences/sales-portal/domain/pricing/screens/quotes/components/quote-name-editor';
import { QuoteSummaryBottomBar } from '@/experiences/sales-portal/domain/pricing/screens/quotes/components/quote-summary-bottom-bar';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import {
  SalesHubAccountOption,
  useAccountListLoader,
} from '@/experiences/sales-portal/blocs/loaders/use-account-list-loader';
import { SalesHubShellTemplate } from '@/experiences/sales-portal/templates/sales-hub-shell-template';
import { SalesPortalTabParams } from '@/navigators/types';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useExperienceUIStore } from '@experiences/blocs/stores/experience-ui-store';
import { useExportQuoteMutator } from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/mutators/use-export-quote-mutator';
import { useLoadSavedQuote } from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/loaders/use-saved-quote-loader';
import { useQuoteState } from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/hooks/use-quote-state';
import { useQuoteUrlSync } from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/hooks/use-quote-url-sync';
import { useSavePriceQuoteMutator } from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/mutators/use-save-price-quote-mutator';

type Props = BottomTabScreenProps<
  SalesPortalTabParams,
  typeof SALES_PORTAL_ROUTES.PriceQuotesBuilder
>;

// Aligns the content gutter with the top bar's flat header gutter.
const HORIZONTAL_PADDING = 24;

/**
 * Shell for the price quotes experience. Renders:
 * - editor header (quote name + Load Quote dropdown + action buttons)
 * - account (customer) selector + empty state when no account is picked
 * - catalog item search (combobox) for adding catalog line items
 * - "+ Custom Item" trigger that opens the custom item panel
 * - "+ Add Competitor" trigger that names a competitor; once set, the
 *   line-items table splits into side-by-side standard / competitor views
 * - skeleton while a saved quote's detail is in flight, then the
 *   line-items table with inline-editable price / quantity + remove action
 * - pinned summary bar with running totals (shown once there's at least
 *   one item)
 *
 * Sidebar entry is gated on the PRICE_QUOTES_BUILDER experiment.
 */
export const PriceQuotesScreen: React.FC<Props> = ({ navigation, route }) => {
  const [styles, theme] = useStyles();
  const { layoutSize } = useLayoutContext();
  const { canSave, dispatch, state, totals } = useQuoteState();
  const { accounts } = useAccountListLoader(
    partnerSupplier.businessOrganizationUUID,
  );
  const { savedQuotes } = useSavedQuotesLoader(
    partnerSupplier.businessOrganizationUUID,
  );
  const loadSavedQuote = useLoadSavedQuote();
  const handleUrlUpdate = useCallback(
    (priceQuoteId: string | null) =>
      navigation.setParams({ priceQuoteUUID: priceQuoteId ?? undefined }),
    [navigation],
  );
  const { save, saving } = useSavePriceQuoteMutator({ dispatch, state });
  const { exportCsv, exportPdf, isExportingCsv, isExportingPdf } =
    useExportQuoteMutator({
      chatId: state.account?.chatId ?? null,
      quoteId: state.quoteId,
    });
  const { alert } = useAlertContext('price_quotes_unsaved_changes');
  const { alert: alertNameQuote } = useAlertContext('price_quotes_name_quote');
  const { showErrorToast, showToast } = useExperienceToasts();
  const [isAccountSelectorOpen, setIsAccountSelectorOpen] = useState(false);
  const [isCustomPanelOpen, setIsCustomPanelOpen] = useState(false);
  const isSidebarCollapsed = useExperienceUIStore((s) => s.isSidebarCollapsed);
  // ensures wide item search bar on smaller displays when nav panel is open
  const isCompactLayout =
    layoutSize === LayoutSize.SMALL ||
    layoutSize === LayoutSize.SMALL_TABLET ||
    (Platform.OS !== 'web' && !isSidebarCollapsed);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [bottomBarHeight, setBottomBarHeight] = useState(0);
  const onBottomBarLayout = useCallback(
    (event: LayoutChangeEvent) =>
      setBottomBarHeight(event.nativeEvent.layout.height),
    [],
  );
  const isBottomBarVisible = totals.itemCount > 0;
  const tableBodyContainerStyle = useMemo(
    () =>
      isBottomBarVisible && bottomBarHeight > 0
        ? { paddingBottom: bottomBarHeight + theme.sk.spacing.spaceLarge }
        : undefined,
    [isBottomBarVisible, bottomBarHeight, theme.sk.spacing.spaceLarge],
  );
  // Bumped on every load attempt + on RESET; in-flight loads compare
  // against the current value and discard stale results.
  const loadGenerationRef = useRef(0);

  const hasAccount = state.account !== null;

  // Wraps a destructive editor-replacing action with an unsaved-changes
  // confirmation when the editor is dirty. Skips the prompt on a clean
  // editor.
  const confirmDiscardThen = useCallback(
    (proceed: () => void) => {
      if (!state.isDirty) {
        proceed();
        return;
      }
      alert({
        buttons: [
          { action: () => {}, style: 'cancel', title: i18n.t('cancel') },
          {
            action: proceed,
            style: 'destructive',
            title: i18n.t(
              'sales_portal.price_quotes.unsaved_changes.discard_cta',
            ),
          },
        ],
        message: i18n.t('sales_portal.price_quotes.unsaved_changes.message'),
        title: i18n.t('sales_portal.price_quotes.unsaved_changes.title'),
      });
    },
    [alert, state.isDirty],
  );

  // First time saving of a quote shows a prompt for the quote name
  const showNameQuoteAlert = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      alertNameQuote({
        buttons: [
          { action: () => {}, style: 'cancel', title: i18n.t('cancel') },
          {
            action: () => {
              if (trimmed === '') {
                return;
              }
              // Dispatch updates the editor UI; pass `trimmed` to `save` so the
              // mutation doesn't race the reducer commit.
              dispatch({ name: trimmed, type: 'SET_NAME' });
              save({ name: trimmed });
            },
            // Disable the save button if the quote name is empty
            disabled: trimmed === '',
            style: 'default',
            title: i18n.t('sales_portal.price_quotes.save'),
          },
        ],
        content: (
          <NameQuoteModalContent
            onChange={(next) => showNameQuoteAlert(next)}
            value={value}
          />
        ),
        message: '',
        title: i18n.t('sales_portal.price_quotes.name_quote.title'),
      });
    },
    [alertNameQuote, dispatch, save],
  );

  const handleSave = useCallback(() => {
    if (state.quoteId !== null) {
      save();
      return;
    }
    showNameQuoteAlert(state.name);
  }, [save, showNameQuoteAlert, state.name, state.quoteId]);

  const handleNewQuote = useCallback(() => {
    confirmDiscardThen(() => {
      // Invalidate any in-flight saved-quote load so its late dispatch
      // can't clobber the reset.
      loadGenerationRef.current += 1;
      setIsLoadingQuote(false);
      dispatch({ type: 'RESET' });
    });
  }, [confirmDiscardThen, dispatch]);

  const duplicateQuote = useCallback(() => {
    dispatch({
      suffix: i18n.t('sales_portal.price_quotes.duplicate.copy_suffix'),
      type: 'DUPLICATE_QUOTE',
    });
    showToast({
      description: i18n.t('sales_portal.price_quotes.duplicate.toast.body'),
      title: i18n.t('sales_portal.price_quotes.duplicate.toast.title'),
      variant: 'success',
    });
  }, [dispatch, showToast]);

  // If the user tries to duplicate a quote but has unsaved changes prompt them
  // via the unsaved changes dialog
  const handleDuplicate = useCallback(() => {
    if (!state.isDirty) {
      duplicateQuote();
      return;
    }
    alert({
      buttons: [
        { action: () => {}, style: 'cancel', title: i18n.t('cancel') },
        {
          action: async () => {
            if (await save()) {
              duplicateQuote();
            }
          },
          style: 'default',
          title: i18n.t('sales_portal.price_quotes.duplicate.unsaved.cta'),
        },
      ],
      message: i18n.t('sales_portal.price_quotes.duplicate.unsaved.message'),
      title: i18n.t('sales_portal.price_quotes.duplicate.unsaved.title'),
    });
  }, [alert, duplicateQuote, save, state.isDirty]);

  // Exports are generated against the saved DB record so save it first, then export
  const saveThenExport = useCallback(
    async (exportFn: () => Promise<void>) => {
      if (state.isDirty) {
        const ok = await save({ silent: true });
        if (!ok) {
          return;
        }
      }
      return exportFn();
    },
    [save, state.isDirty],
  );

  // Switching customer mid-quote (with line items present) prompts to
  // confirm before clearing items. Distinct from the "discard changes"
  // confirmation because this flow keeps `quoteId` — the rep is editing
  // the same saved quote, just changing who it's for.
  const confirmSwitchAccount = useCallback(
    (account: SalesHubAccountOption, proceed: () => void) => {
      alert({
        buttons: [
          { action: () => {}, style: 'cancel', title: i18n.t('cancel') },
          {
            action: proceed,
            style: 'destructive',
            title: i18n.t('sales_portal.price_quotes.switch_account.cta'),
          },
        ],
        message: i18n.t('sales_portal.price_quotes.switch_account.message', {
          accountName: account.name,
        }),
        title: i18n.t('sales_portal.price_quotes.switch_account.title'),
      });
    },
    [alert],
  );

  const handleSelectAccount = useCallback(
    (account: SalesHubAccountOption) => {
      // First selection — no quote to mutate, just set it.
      if (state.account === null) {
        dispatch({ account, type: 'SET_ACCOUNT' });
        return;
      }

      const proceed = () => {
        // Cancel any in-flight saved-quote load so its late dispatch
        // can't clobber the switch.
        loadGenerationRef.current += 1;
        setIsLoadingQuote(false);
        dispatch({ account, type: 'SWITCH_ACCOUNT' });
      };

      // Prompt if there's meaningful work the switch would discard.
      // SWITCH_ACCOUNT preserves a custom title automatically, so the
      // name doesn't need to factor in here.
      const hasMeaningfulEdits =
        state.lineItems.length > 0 || state.competitorName !== null;

      if (!hasMeaningfulEdits) {
        proceed();
        return;
      }

      confirmSwitchAccount(account, proceed);
    },
    [
      confirmSwitchAccount,
      dispatch,
      state.account,
      state.competitorName,
      state.lineItems.length,
    ],
  );

  const loadQuote = useCallback(
    async (priceQuoteId: string) => {
      const myGen = ++loadGenerationRef.current;
      const isStale = () => myGen !== loadGenerationRef.current;
      setIsLoadingQuote(true);
      try {
        const loaded = await loadSavedQuote(priceQuoteId);
        if (isStale()) {
          return;
        }
        if (loaded) {
          dispatch({ state: loaded, type: 'LOAD_QUOTE' });
        } else {
          showErrorToast(i18n.t('sales_portal.price_quotes.load_quote.error'));
        }
      } catch {
        if (isStale()) {
          return;
        }
        showErrorToast(i18n.t('sales_portal.price_quotes.load_quote.error'));
      } finally {
        if (!isStale()) {
          setIsLoadingQuote(false);
        }
      }
    },
    [dispatch, loadSavedQuote, showErrorToast],
  );

  const confirmThenLoadQuote = useCallback(
    (priceQuoteId: string) => confirmDiscardThen(() => loadQuote(priceQuoteId)),
    [confirmDiscardThen, loadQuote],
  );

  const handleSelectQuote = useCallback(
    (summary: PriceQuoteSummary) => confirmThenLoadQuote(summary.priceQuoteId),
    [confirmThenLoadQuote],
  );

  useQuoteUrlSync({
    loadedQuoteId: state.quoteId,
    onLoadQuote: confirmThenLoadQuote,
    onUrlUpdate: handleUrlUpdate,
    urlQuoteId: route.params?.priceQuoteUUID,
  });

  // Stable handler references so `QuoteLineItemsTable`'s `columns` useMemo
  // keeps its identity across screen re-renders. Without this, every cell
  // edit would reset TanStack Table's internal state because columns would
  // rebuild. `dispatch` is stable per React's useReducer contract.
  const handleRemoveItem = useCallback(
    (localId: string) => dispatch({ localId, type: 'REMOVE_ITEM' }),
    [dispatch],
  );
  const handleUpdatePrice = useCallback(
    (localId: string, price: string) =>
      dispatch({ localId, price, type: 'UPDATE_PRICE' }),
    [dispatch],
  );
  const handleUpdateQuantity = useCallback(
    (localId: string, quantity: string) =>
      dispatch({ localId, quantity, type: 'UPDATE_QUANTITY' }),
    [dispatch],
  );
  const handleUpdateCompetitor = useCallback(
    (localId: string, competitor: CompetitorData) =>
      dispatch({ competitor, localId, type: 'UPDATE_COMPETITOR' }),
    [dispatch],
  );
  const handleAddCompetitor = useCallback(
    (name: string) => dispatch({ name, type: 'SET_COMPETITOR_NAME' }),
    [dispatch],
  );
  const handleRemoveCompetitor = useCallback(
    () => dispatch({ name: null, type: 'SET_COMPETITOR_NAME' }),
    [dispatch],
  );
  const handleAddCatalogItem = useCallback(
    (item: Omit<QuoteLineItem, 'currencyCode' | 'localId' | 'sortOrder'>) =>
      dispatch({ item, type: 'ADD_ITEM' }),
    [dispatch],
  );
  const handleSearchError = useCallback(() => {
    showErrorToast(i18n.t('sales_portal.price_quotes.search_error'));
  }, [showErrorToast]);

  return (
    <>
      <SalesHubShellTemplate>
        <AppPageLayout
          actions={
            <QuoteActionButtons
              accountSelected={hasAccount}
              canSave={canSave}
              isExportingCsv={isExportingCsv}
              isExportingPdf={isExportingPdf}
              onDuplicate={handleDuplicate}
              onExportCsv={() => saveThenExport(exportCsv)}
              onExportPdf={() => saveThenExport(exportPdf)}
              onNewQuote={handleNewQuote}
              onSave={handleSave}
              onSelectQuote={handleSelectQuote}
              quoteId={state.quoteId}
              savedQuotes={savedQuotes}
              saving={saving}
            />
          }
          leadingActions={
            <QuoteNameEditor
              onNameChange={(name) => dispatch({ name, type: 'SET_NAME' })}
              quoteName={state.name}
            />
          }
          scroll={false}
        >
          <View style={styles.container}>
            <View
              style={[
                styles.setupColumn,
                { paddingHorizontal: HORIZONTAL_PADDING },
              ]}
            >
              <View style={styles.accountRow}>
                <AccountSelector
                  account={state.account}
                  accounts={accounts}
                  isOpen={isAccountSelectorOpen}
                  onOpenChange={setIsAccountSelectorOpen}
                  onSelectAccount={handleSelectAccount}
                />
                <AddCompetitor
                  competitorName={state.competitorName}
                  disabled={!hasAccount}
                  onAddCompetitor={handleAddCompetitor}
                  onRemoveCompetitor={handleRemoveCompetitor}
                />
              </View>
              <View style={styles.divider}>
                <Divider />
              </View>
              {state.account ? (
                <View
                  style={[
                    styles.searchRow,
                    isCompactLayout && styles.searchRowCompact,
                  ]}
                >
                  <View style={styles.searchWrapper}>
                    <QuoteItemSearch
                      chatId={state.account.chatId}
                      // Remount on account change so the loader's debounce / results
                      // / search text reset — otherwise the prior account's catalog
                      // hits stay on screen.
                      key={state.account.chatId}
                      lineItems={state.lineItems}
                      onAddItem={handleAddCatalogItem}
                      onRemoveItem={handleRemoveItem}
                      onSearchError={handleSearchError}
                    />
                  </View>
                  <Button
                    accessibilityLabel={i18n.t(
                      'sales_portal.price_quotes.add_custom_item',
                    )}
                    icon={<Icons.Plus size={16} />}
                    onPress={() => setIsCustomPanelOpen(true)}
                    size="$md"
                    variant="secondary"
                  >
                    {i18n.t('sales_portal.price_quotes.add_custom_item')}
                  </Button>
                </View>
              ) : null}
            </View>
            <View style={styles.contentArea}>
              {isLoadingQuote ? (
                <QuoteLineItemsTableSkeleton />
              ) : hasAccount ? (
                <QuoteLineItemsTable
                  accountName={state.account?.name ?? ''}
                  bodyContainerStyle={tableBodyContainerStyle}
                  competitorName={state.competitorName}
                  lineItems={state.lineItems}
                  onRemoveItem={handleRemoveItem}
                  onUpdateCompetitor={handleUpdateCompetitor}
                  onUpdatePrice={handleUpdatePrice}
                  onUpdateQuantity={handleUpdateQuantity}
                />
              ) : (
                <PriceQuotesEmptyState
                  onPress={() => setIsAccountSelectorOpen(true)}
                />
              )}
            </View>
            {state.account !== null && isBottomBarVisible ? (
              <QuoteSummaryBottomBar
                accountName={state.account.name}
                currencyCode={state.account.currencyCode}
                isCompetitive={state.competitorName !== null}
                itemCount={totals.itemCount}
                onLayout={onBottomBarLayout}
                totalAmountInMicros={totals.totalAmountInMicros}
                totalSavingsInMicros={totals.totalSavingsInMicros}
              />
            ) : null}
          </View>
        </AppPageLayout>
      </SalesHubShellTemplate>
      {/*
       * Rendered outside the template as an overlay sibling. AnimatePresence
       * plays the SidePanel's exit animation before unmounting when
       * `isCustomPanelOpen` flips to false.
       */}
      <AnimatePresence>
        {isCustomPanelOpen ? (
          <CustomItemPanel
            onAddItem={(item) => dispatch({ item, type: 'ADD_CUSTOM_ITEM' })}
            onClose={() => setIsCustomPanelOpen(false)}
          />
        ) : null}
      </AnimatePresence>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  accountRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    zIndex: 0,
  },
  container: {
    flex: 1,
    gap: theme.sk.spacing.spaceMedium,
    paddingTop: theme.sk.spacing.spaceMedium,
  },
  contentArea: {
    flex: 1,
    paddingHorizontal: HORIZONTAL_PADDING,
  },
  divider: {
    alignSelf: 'stretch',
  },
  searchRow: {
    alignItems: 'center',
    alignSelf: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    width: '50%',
  },
  searchRowCompact: {
    alignSelf: 'stretch',
    width: '100%',
  },
  searchWrapper: {
    flex: 1,
  },
  setupColumn: {
    alignItems: 'flex-start',
    flexDirection: 'column',
    gap: theme.sk.spacing.spaceMedium,
    zIndex: 1,
  },
}));
